import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, CreditCard, FileText, FlaskConical, Gift, Rocket, Truck } from 'lucide-react';
import useStoreSetupStatus from '../../../hooks/useStoreSetupStatus';
import { updateStore } from '../../../utils/queries';
import DialogModal from '../../../components/dialog';
import PaceConnectPanel from './PaceConnectPanel';

function ProgressBar({ completed, total }) {
	const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
	return (
		<div>
			<div className='h-2 w-full bg-gray-200 rounded-full overflow-hidden'>
				<div className='h-full bg-black rounded-full transition-all' style={{ width: `${pct}%` }} />
			</div>
			<p className='text-xs text-gray-400 mt-1'>
				{completed}/{total} steps complete
			</p>
		</div>
	);
}

function SectionCard({ title, done, description, disabled, disabledNote, manageLinks, children }) {
	return (
		<div className={`border border-gray-200 rounded-lg p-5 ${disabled ? 'opacity-50' : ''}`}>
			<div className='flex items-start justify-between gap-4 mb-3'>
				<div className='flex items-start gap-2'>
					{done !== undefined &&
						(done ? (
							<CheckCircle2 className='size-5 text-green-600 shrink-0' />
						) : (
							<Circle className='size-5 text-gray-300 shrink-0' />
						))}
					<div>
						<h2 className='text-base font-medium'>{title}</h2>
						{description && <p className='text-sm text-gray-500 mt-0.5'>{description}</p>}
						{disabled && disabledNote && <p className='text-xs text-gray-400 mt-0.5'>{disabledNote}</p>}
					</div>
				</div>
				{!disabled && manageLinks && <div className='flex gap-2 shrink-0'>{manageLinks}</div>}
			</div>
			<div className='ml-7'>
				{!disabled && children}
			</div>
		</div>
	);
}

export default function StoreSetup() {
	const navigate = useNavigate();
	const status = useStoreSetupStatus();
	const [switchingLive, setSwitchingLive] = useState(false);
	const [confirmLive, setConfirmLive] = useState(false);
	const [editingPace, setEditingPace] = useState(false);

	if (status.loading) {
		return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;
	}

	if (!status.store) {
		return (
			<div className='py-8 pr-8'>
				<p className='text-sm text-gray-500'>No store selected.</p>
			</div>
		);
	}

	const {
		store,
		usesPace,
		paceAccount,
		defaults,
		shippingMappings,
		paymentMethodRows,
		loyaltyProgramRows,
		paymentTermMappingRows,
		productsTotal,
		sections,
		allDone,
	} =
		status;

	const nextStep = sections.find((s) => !s.done);
	const sectionDone = (key) => sections.find((s) => s.key === key)?.done;

	const scrollTo = (key) => {
		document.getElementById(`setup-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const handleGoLive = async () => {
		setSwitchingLive(true);
		await updateStore(store.storeId, { mode: 'live' });
		setSwitchingLive(false);
		setConfirmLive(false);
		status.refresh();
	};

	const handleGoTest = async () => {
		await updateStore(store.storeId, { mode: 'test' });
		status.refresh();
	};

	return (
		<div className='py-8 pr-8 max-w-6xl'>
			<div className='flex gap-8 items-start'>
			<div className='flex-1 min-w-0'>
				{/* Row 1 */}
				<div className='mb-6'>
					<h1 className='text-2xl font-light tracking-tight'>Finish store setup</h1>
					<p className='text-sm text-gray-500 mt-1 mb-4'>
						Complete the essentials to finish {store.storeName} and start accepting orders.
					</p>
					<ProgressBar completed={sections.filter((s) => s.done).length} total={sections.length} />
				</div>

				{/* Row 2 */}
				{nextStep && (
					<div className='flex items-center justify-between border border-gray-200 rounded-lg px-5 py-4 mb-6 bg-gray-50'>
						<div>
							<p className='text-xs uppercase tracking-wide text-gray-400'>Next step</p>
							<p className='text-sm font-medium'>{nextStep.label}</p>
							<p className='text-sm font-normal text-gray-500'>{nextStep.description}</p>
						</div>
						<button type='button' className='btn-primary' onClick={() => scrollTo(nextStep.key)}>
							Continue
						</button>
					</div>
				)}

				<div className='space-y-4'>
				{/* Row 3 */}
				<div id='setup-pace'>
					<SectionCard
						title='ERP details'
						done={sectionDone('pace')}
						manageLinks={
							sectionDone('pace') &&
							!editingPace && (
								<button type='button' className='btn-secondary-small' onClick={() => setEditingPace(true)}>
									Edit
								</button>
							)
						}
					>
						{sectionDone('pace') && !editingPace ? (
							<div className='grid grid-cols-2 gap-x-6 gap-y-2 text-sm'>
								<p>
									<span className='text-gray-500'>ERP Customer Number:</span> {paceAccount?.paceCustomerNumber || '—'}
								</p>
								<p>
									<span className='text-gray-500'>CSR ID:</span> {defaults?.erpCsrId || '—'}
								</p>
								<p>
									<span className='text-gray-500'>Salesperson ID:</span> {defaults?.erpSalespersonId || '—'}
								</p>
								<p>
									<span className='text-gray-500'>Job Type ID:</span> {defaults?.erpJobTypeId || '—'}
								</p>
								<p>
									<span className='text-gray-500'>Manufacturing Location ID:</span> {defaults?.erpManufacturingLocationId || '—'}
								</p>
							</div>
						) : (
							<PaceConnectPanel
								storeId={store.storeId}
								onConnected={() => {
									setEditingPace(false);
									status.refresh();
								}}
							/>
						)}
					</SectionCard>
				</div>

				{/* Row 4 */}
				<div id='setup-identity'>
					<SectionCard
						title='Store identity'
						done={sectionDone('identity')}
						description='Name, logo, and brand basics.'
						manageLinks={
							<button type='button' className='btn-secondary-small' onClick={() => navigate('/settings/branding')}>
								Manage
							</button>
						}
					>
						<p className='text-sm text-gray-500'>
							{store.storeName} {status.logoUrl ? ' · logo uploaded' : ' · no logo uploaded yet'}
						</p>
					</SectionCard>
				</div>

				{/* Row 5 */}
				<div id='setup-products'>
					<SectionCard
						title='Products'
						done={sectionDone('products')}
						description='Add products to your store.'
						manageLinks={
							<button type='button' className='btn-secondary-small' onClick={() => navigate('/products')}>
								Manage
							</button>
						}
					>
						<p className='text-sm text-gray-500'>
							{productsTotal > 0 ? `${productsTotal} product${productsTotal === 1 ? '' : 's'} ready to sell.` : 'No products yet.'}
						</p>
					</SectionCard>
				</div>

				{/* Row 6 */}
				<div id='setup-shipping'>
					<SectionCard
						title='Shipping methods'
						done={sectionDone('shipping')}
						description='Select the shipping options you want to offer:'
						disabled={!usesPace}
						disabledNote="This store doesn't use ERP fulfillment."
						manageLinks={
							<button type='button' className='btn-secondary-small' onClick={() => navigate('/settings/shipping-methods')}>
								Manage
							</button>
						}
					>
						{shippingMappings.length > 0 ? (
							<div className='space-y-2'>
								{shippingMappings.map((m) => (
									<div
										key={m.shippingMethodMappingId}
										className='flex items-center gap-2 border border-gray-200 rounded-md px-3 py-2 text-sm'
									>
										<Truck className='size-4 text-gray-400' />
										{m.shippingTitle}
									</div>
								))}
							</div>
						) : (
							<p className='text-sm text-gray-500'>No shipping methods configured yet.</p>
						)}
					</SectionCard>
				</div>

				{/* Row 7 */}
				<div id='setup-payments'>
					<SectionCard
						title='Payments'
						done={sectionDone('payments')}
						manageLinks={
							<button type='button' className='btn-secondary-small' onClick={() => navigate('/settings/payment-methods')}>
								Manage
							</button>
						}
					>
						<p className='text-sm text-gray-500 mb-3'>
							Connect Stripe to accept credit cards, digital wallets, and other payment methods. Stripe handles payments
							securely and supports cards, Apple Pay, Google Pay, and more.
						</p>
						{paymentMethodRows.length > 0 || loyaltyProgramRows.length > 0 || paymentTermMappingRows.length > 0 ? (
							<div className='flex gap-2 flex-wrap'>
								{paymentMethodRows.map((m) => (
									<span
										key={m.storePaymentMethodId}
										className='inline-flex items-center gap-1 text-xs bg-gray-100 rounded-full px-2.5 py-1'
									>
										<CreditCard className='size-3.5' />
										{m.paymentMethod === 'CREDIT_CARD' ? 'Credit Card' : 'Purchase Order'}
									</span>
								))}
								{paymentTermMappingRows.length > 0 && (
									<span className='inline-flex items-center gap-1 text-xs bg-gray-100 rounded-full px-2.5 py-1'>
										<FileText className='size-3.5' />
										Invoice
									</span>
								)}
								{loyaltyProgramRows.map((l) => (
									<span
										key={l.storeLoyaltyProgramId}
										className='inline-flex items-center gap-1 text-xs bg-gray-100 rounded-full px-2.5 py-1'
									>
										<Gift className='size-3.5' />
										Loyalty Points
									</span>
								))}
							</div>
						) : (
							<p className='text-sm text-gray-500'>No payment methods enabled yet.</p>
						)}
					</SectionCard>
				</div>

				{/* Row 8 */}
				<div id='setup-launch'>
					<SectionCard title='Launch your store' description='Review your setup and start accepting real orders.'>
						<div className='grid grid-cols-2 gap-4'>
							<button
								type='button'
								onClick={handleGoTest}
								className={`text-left border rounded-lg p-4 ${
									store.mode !== 'live' ? 'border-black' : 'border-gray-200 hover:bg-gray-50'
								}`}
							>
								<div className='flex items-center gap-2 mb-1'>
									<FlaskConical className='size-4 text-gray-600' />
									<span className='font-medium text-sm'>Test Mode</span>
									{store.mode !== 'live' && <span className='text-xs text-gray-400'>Current</span>}
								</div>
								<p className='text-xs text-gray-500'>Emails, webhooks and workflows run, but orders are not submitted to the ERP (Pace).</p>
							</button>
							<button
								type='button'
								onClick={() => setConfirmLive(true)}
								className={`text-left border rounded-lg p-4 ${
									store.mode === 'live' ? 'border-black' : 'border-gray-200 hover:bg-gray-50'
								}`}
							>
								<div className='flex items-center gap-2 mb-1'>
									<Rocket className='size-4 text-gray-600' />
									<span className='font-medium text-sm'>Live Mode</span>
									{store.mode === 'live' && <span className='text-xs text-gray-400'>Current</span>}
								</div>
								<p className='text-xs text-gray-500'>Orders are submitted to the connected Pace ERP as real jobs.</p>
							</button>
						</div>
					</SectionCard>
				</div>
				</div>
			</div>

			{/* Launch readiness sidebar */}
			<div className='w-72 shrink-0 sticky top-8'>
				<div className='border border-gray-200 rounded-lg p-5'>
					<h2 className='text-base font-medium mb-1'>Launch readiness</h2>
					<p className='text-sm text-gray-500 mb-4'>
						{allDone ? 'All steps complete.' : `${sections.filter((s) => !s.done).length} step(s) left.`}
					</p>
					<ul className='space-y-2'>
						{sections.map((s) => (
							<li key={s.key} className='flex items-center gap-2 text-sm'>
								{s.done ? (
									<CheckCircle2 className='size-4 text-green-600 shrink-0' />
								) : (
									<Circle className='size-4 text-gray-300 shrink-0' />
								)}
								<button type='button' className='hover:underline text-left' onClick={() => scrollTo(s.key)}>
									{s.label}
								</button>
							</li>
						))}
					</ul>
				</div>
			</div>
			</div>

			<DialogModal open={confirmLive} setOpen={setConfirmLive} title='Go live?'>
				<p className='text-sm text-gray-600'>
					This starts accepting real orders from customers, with payments processed and charged. Continue?
				</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setConfirmLive(false)} disabled={switchingLive}>
						Cancel
					</button>
					<button type='button' className='btn-primary' onClick={handleGoLive} disabled={switchingLive}>
						{switchingLive ? 'Switching...' : 'Go Live'}
					</button>
				</div>
			</DialogModal>
		</div>
	);
}
